/**
 * Canadian Cities Data Utility
 * Fetches cities from database via API and provides helper functions
 * 
 * This module provides timezone data for Canadian cities synchronized with the admin panel.
 * Cities are fetched from the backend API which is managed through the admin Cities Settings.
 */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// Timezone display names
export const timezoneDisplayNames = {
  'America/St_Johns': 'Newfoundland Time (NST/NDT)',
  'America/Halifax': 'Atlantic Time (AST/ADT)',
  'America/Moncton': 'Atlantic Time (AST/ADT)',
  'America/Toronto': 'Eastern Time (EST/EDT)',
  'America/Montreal': 'Eastern Time (EST/EDT)',
  'America/Winnipeg': 'Central Time (CST/CDT)',
  'America/Regina': 'Central Standard Time (CST)',
  'America/Edmonton': 'Mountain Time (MST/MDT)',
  'America/Vancouver': 'Pacific Time (PST/PDT)',
  'America/Whitehorse': 'Mountain Standard Time (MST)',
  'America/Yellowknife': 'Mountain Time (MST/MDT)',
  'America/Iqaluit': 'Eastern Time (EST/EDT)',
  'America/Rankin_Inlet': 'Central Time (CST/CDT)',
  'America/Cambridge_Bay': 'Mountain Time (MST/MDT)',
  'America/Goose_Bay': 'Atlantic Time (AST/ADT)',
  'America/Creston': 'Mountain Standard Time (MST)',
  'America/Dawson_Creek': 'Mountain Standard Time (MST)',
  'America/Fort_Nelson': 'Mountain Standard Time (MST)',
  'America/Thunder_Bay': 'Eastern Time (EST/EDT)',
  'America/Rainy_River': 'Central Time (CST/CDT)',
  // Test timezone - Turkey
  'Europe/Istanbul': '🧪 TEST: Turkey Time (TRT, UTC+3)',
};

// Cache for cities data
let citiesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all Canadian cities from API
 * @returns {Promise<Array>} Array of city objects
 */
export const fetchCities = async () => {
  // Check cache
  if (citiesCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return citiesCache;
  }

  try {
    const response = await fetch(`${API_BASE}/api/cities/by-country/Canada`);
    if (response.ok) {
      const data = await response.json();
      citiesCache = data.cities || [];
      cacheTimestamp = Date.now();
      return citiesCache;
    }
  } catch (error) {
    console.error('Error fetching cities:', error);
  }
  
  return citiesCache || [];
};

/**
 * Clear the cities cache to force a refresh
 */
export const clearCitiesCache = () => {
  citiesCache = null;
  cacheTimestamp = null;
};

/**
 * Get cities formatted for dropdown display
 * Format: "City, Province (Timezone Display Name)"
 * @returns {Promise<Array>} Array of option objects for dropdown
 */
export const getCityOptions = async () => {
  const cities = await fetchCities();
  
  return cities.map(city => ({
    value: `${city.city}|${city.province}|${city.timezone}`,
    label: `${city.city}, ${city.province} (${timezoneDisplayNames[city.timezone] || city.timezone})`,
    city: city.city,
    province: city.province,
    timezone: city.timezone,
    timezoneDisplay: timezoneDisplayNames[city.timezone] || city.timezone,
    population: city.population || 0,
  }));
};

/**
 * Parse a city value back to its components
 * @param {string} value - The combined value string "city|province|timezone"
 * @returns {Object|null} Object with city, province, timezone or null
 */
export const parseCityValue = (value) => {
  if (!value) return null;
  const [city, province, timezone] = value.split('|');
  return { city, province, timezone };
};

/**
 * Get timezone display name
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Human readable timezone name
 */
export const getTimezoneDisplayName = (timezone) => {
  return timezoneDisplayNames[timezone] || timezone;
};

/**
 * Search cities by query (case-insensitive)
 * @param {string} query - Search query
 * @param {Array} cities - Array of city options (from getCityOptions)
 * @returns {Array} Filtered city options
 */
export const searchCities = (query, cities) => {
  if (!query) return cities.slice(0, 50); // Return top 50 by population
  
  const lowerQuery = query.toLowerCase();
  return cities.filter(option => 
    option.city.toLowerCase().includes(lowerQuery) ||
    option.province.toLowerCase().includes(lowerQuery)
  ).slice(0, 100); // Limit results for performance
};

/**
 * Get all available timezones for Canada
 * @returns {Array} Array of timezone option objects
 */
export const getCanadaTimezones = () => {
  return Object.entries(timezoneDisplayNames).map(([value, label]) => ({
    value,
    label
  }));
};

export default {
  fetchCities,
  getCityOptions,
  parseCityValue,
  getTimezoneDisplayName,
  searchCities,
  getCanadaTimezones,
  timezoneDisplayNames,
  clearCitiesCache,
};
